import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaMinus, FaPlus, FaSync } from 'react-icons/fa';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { addOrder } from '../../firebase/services';
import styles from './Cart.module.css';
import translationService from '../../services/translationService';

function Cart({ cartItems, removeFromCart, clearCart, updateCartItem, lang = 'ar' }) {
  const navigate = useNavigate();
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isWholesale, setIsWholesale] = useState(() => {
    return JSON.parse(localStorage.getItem('isWholesaleActive')) || false;
  });
  const [hasWholesaleItems, setHasWholesaleItems] = useState(false);
  const [showRefreshTip, setShowRefreshTip] = useState(false);
  const [tipMessage, setTipMessage] = useState('');

  const funnyMessages = [
    "🛍️ Hey! Your shopping cart is ready for a refresh!",
    "🎯 New items waiting to appear - Click to refresh!",
    "🎪 Step right up! Click to see your new items!",
    "🎨 Your cart has new treasures - Click to reveal them!",
    "🎭 Time for the grand reveal - Refresh to see what's new!",
    "✨ Magic happens with a refresh - Click here!"
  ];

  useEffect(() => {
    // Check if there are newly added products
    const hasNewItems = localStorage.getItem('newItemsAdded') === 'true';
    if (hasNewItems) {
      setShowRefreshTip(true);
      setTipMessage(funnyMessages[Math.floor(Math.random() * funnyMessages.length)]);
      // Clear the flag after showing the message
      localStorage.removeItem('newItemsAdded');
    }
  }, []);

  useEffect(() => {
    const handleCartUpdate = () => {
      setShowRefreshTip(true);
      setTipMessage(funnyMessages[Math.floor(Math.random() * funnyMessages.length)]);
    };

    window.addEventListener('cart-updated', handleCartUpdate);
    return () => window.removeEventListener('cart-updated', handleCartUpdate);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateItemPrice = (item) => {
    if (!item) return 0;
    
    if (item.isOffer && typeof item.offerPrice === 'number') {
      return item.offerPrice;
    }
    
    const basePrice = Number(item.price) || 0;
    if (isWholesale && item.quantity >= 12 && item.wholesaleDiscount) {
      const discountPercentage = Number(item.wholesaleDiscount) || 0;
      return basePrice * (1 - (discountPercentage / 100));
    }
    
    return basePrice;
  };

  const validateInputs = () => {
    // Name: only letters and spaces, at least 2 chars
    if (!/^[\p{L} ]{2,}$/u.test(customerInfo.name.trim())) {
      setError('Please enter a valid name (letters and spaces only, at least 2 characters).');
      return false;
    }
    // Phone: only digits, 8-15 digits
    if (!/^\d{8,15}$/.test(customerInfo.phone.trim())) {
      setError('Please enter a valid phone number (8-15 digits).');
      return false;
    }
    // Address: at least 5 characters
    if (customerInfo.address.trim().length < 5) {
      setError('Please enter a valid address (at least 5 characters).');
      return false;
    }
    return true;
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;
    setIsSubmitting(true);
    setError('');
    try {
      const itemsWithFinalPrices = cartItems.map(item => ({
        id: item.id,
        name: item.name || 'Unnamed Product',
        quantity: Number(item.quantity) || 1,
        price: Number(item.price) || 0,
        offerPrice: item.isOffer ? Number(item.offerPrice) || Number(item.price) : null,
        isOffer: Boolean(item.isOffer),
        wholesaleDiscount: Number(item.wholesaleDiscount) || 0,
        isWholesaleApplied: isWholesale && item.quantity >= 12,
        finalPrice: calculateItemPrice(item),
        imageUrl: item.imageUrl || null,
        marketerId: item.marketerId || null,
        size: item.selectedSize || null,
        color: item.selectedColor || null
      }));
      
      // Debug: Log the items to see if size and color are included
      console.log('Cart items with size/color:', itemsWithFinalPrices);
      
      const orderTotal = itemsWithFinalPrices.reduce((sum, item) => 
        sum + (item.finalPrice * item.quantity), 0);
      const orderData = {
        items: itemsWithFinalPrices,
        totalAmount: orderTotal,
        isWholesaleOrder: isWholesale,
        status: 'pending',
        clientName: customerInfo.name.trim(),
        clientPhone: customerInfo.phone.trim(),
        clientAddress: {
          street: customerInfo.address.trim(),
          city: '',
          country: '',
          postalCode: ''
        },
        createdAt: serverTimestamp()
      };
      if (!orderData.totalAmount || orderData.totalAmount <= 0) {
        throw new Error('Invalid order total');
      }
      if (!orderData.items.length) {
        throw new Error('No items in order');
      }
      await addOrder(orderData);
      localStorage.removeItem('isWholesaleActive');
      clearCart();
      navigate('/order-confirmation');
      alert('Order placed successfully!');
    } catch (err) {
      console.error('Error submitting order:', err);
      setError(`Failed to submit order: ${err.message}`);
      alert(`Failed to submit order: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateQuantity = (productKey, newQuantity) => {
    if (newQuantity < 1) return;

    const updatedCart = cartItems.map(item => {
      const itemKey = `${item.id}-${item.selectedSize || 'nosize'}-${item.selectedColor || 'nocolor'}`;
      return itemKey === productKey ? { ...item, quantity: newQuantity } : item;
    });
    
    // Update localStorage
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    
    // Update UI state immediately using the updateCartItem prop
    // Extract the original item ID for the updateCartItem function
    const [itemId] = productKey.split('-');
    updateCartItem(itemId, { quantity: newQuantity });
    
    // Check wholesale eligibility after quantity update
    checkWholesaleEligibility();
  };

  const formatPrice = (price) => {
    if (typeof price !== 'number' || isNaN(price)) {
      return '$0';
    }
    return '$' + price.toLocaleString('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const checkWholesaleEligibility = () => {
    const eligibleItems = cartItems.filter(item => {
      return item.quantity >= 12 && item.wholesaleDiscount > 0;
    });
    setHasWholesaleItems(eligibleItems.length > 0);
  };

  useEffect(() => {
    checkWholesaleEligibility();
  }, [cartItems]);

  const toggleWholesale = () => {
    const newWholesaleState = !isWholesale;
    setIsWholesale(newWholesaleState);
    localStorage.setItem('isWholesaleActive', JSON.stringify(newWholesaleState));
    
    const cartData = JSON.parse(localStorage.getItem('cart')) || [];
    const updatedCart = cartData.map(item => ({
      ...item,
      isWholesaleApplied: newWholesaleState && item.quantity >= 12,
      finalPrice: calculateItemPrice({
        ...item,
        isWholesaleApplied: newWholesaleState && item.quantity >= 12
      })
    }));
    localStorage.setItem('cart', JSON.stringify(updatedCart));
    localStorage.removeItem('newItemsAdded');
    window.dispatchEvent(new Event('cart-updated'));
    alert('Product added to cart successfully!');
  };

  const getItemTotal = (item) => {
    const unitPrice = calculateItemPrice(item);
    return unitPrice * item.quantity;
  };

  const total = cartItems.reduce((sum, item) => sum + getItemTotal(item), 0);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (cartItems.length === 0) {
    return (
      <div className={styles.emptyCart}>
        <h2>The cart is empty</h2>
        <p>You haven't added any products to the cart yet</p>
        <Link to="/shop" className={styles.continueShoppingButton}>
          Back to shopping
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.cart}>
      {showRefreshTip && (
        <div className={styles.refreshTip} onClick={handleRefresh}>
          <FaSync className={styles.refreshIcon} />
          <p>{tipMessage}</p>
        </div>
      )}
      
      <div className={styles.cartHeader}>
        <h1>Shopping Cart</h1>
        <button onClick={clearCart} className={styles.clearCartButton}>
          <FaTrash />
          <span>Empty Cart</span>
        </button>
      </div>

      {cartItems.some(item => item.quantity >= 12) && (
        <div className={styles.wholesaleToggle}>
          <button
            className={`${styles.wholesaleButton} ${isWholesale ? styles.active : ''}`}
            onClick={toggleWholesale}
          >
            {isWholesale ? 'Disable Wholesale Pricing' : "I'm a Wholesaler"}
          </button>
          <div className={styles.wholesaleInfo}>
            {isWholesale ? (
              <span className={styles.wholesaleActive}>
                Wholesale pricing applied to eligible items
              </span>
            ) : (
              <span>
                {cartItems.some(item => item.quantity >= 12) 
                  ? 'Click to apply wholesale pricing'
                  : 'Minimum 12 units required for wholesale pricing'}
              </span>
            )}
          </div>
        </div>
      )}

      <div className={styles.cartContent}>
        <div className={styles.cartItems}>
          {cartItems.map(item => (
            <div key={`${item.id}-${item.selectedSize || 'nosize'}-${item.selectedColor || 'nocolor'}`} className={`${styles.cartItem} ${
              isWholesale && item.quantity >= 12 ? styles.wholesaleItem : ''
            }`}>
              <div className={styles.itemImage}>
                <img src={item.imageUrl} alt={item.name} />
              </div>
              <div className={styles.itemInfo}>
                <h3>{translationService.getTranslatedContent(item, 'name', lang) || item.name || ''}</h3>
                {/* Product description in correct language */}
                <p className={styles.productDescription}>
                  {translationService.getTranslatedContent(item, 'description', lang) || item.description || ''}
                </p>
                <div className={styles.itemDetails}>
                  {item.selectedSize && (
                    <p className={styles.itemSize}>Size: <strong>{item.selectedSize}</strong></p>
                  )}
                  {item.selectedColor && (
                    <p className={styles.itemColor}>Color: <strong>{item.selectedColor}</strong></p>
                  )}
                </div>
                <div className={styles.priceInfo}>
                  {item.isOffer ? (
                    <>
                      <span className={styles.originalPrice}>{formatPrice(item.originalPrice)}</span>
                      <span className={styles.offerPrice}>{formatPrice(item.price)}</span>
                    </>
                  ) : isWholesale && item.quantity >= 12 && item.wholesaleDiscount ? (
                    <>
                      <span className={styles.originalPrice}>
                        {formatPrice(item.price)}
                      </span>
                      <span className={styles.wholesalePrice}>
                        {formatPrice(calculateItemPrice(item))}
                        <small> ({item.wholesaleDiscount}% off)</small>
                      </span>
                    </>
                  ) : (
                    <span className={styles.itemPrice}>
                      {formatPrice(item.price)}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.itemActions}>
                <div className={styles.quantityControls}>
                  <button 
                    onClick={() => updateQuantity(`${item.id}-${item.selectedSize || 'nosize'}-${item.selectedColor || 'nocolor'}`, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    <FaMinus />
                  </button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(`${item.id}-${item.selectedSize || 'nosize'}-${item.selectedColor || 'nocolor'}`, item.quantity + 1)}>
                    <FaPlus />
                  </button>
                </div>
                <button 
                  onClick={() => removeFromCart(`${item.id}-${item.selectedSize || 'nosize'}-${item.selectedColor || 'nocolor'}`)}
                  className={styles.removeButton}
                >
                  <FaTrash />
                </button>
              </div>
              <div className={styles.itemTotal}>
                {formatPrice(getItemTotal(item))}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.cartSummary}>
          <h2>Order Summary</h2>
          <div className={styles.summaryRow}>
            <span>Subtotal</span>
            <span>{formatPrice(total)}</span>
          </div>
          {isWholesale && (
            <div className={styles.wholesaleDiscount}>
              <span>Wholesale Pricing Applied</span>
              <span className={styles.discountTag}>Active</span>
            </div>
          )}
          <div className={styles.summaryRow}>
            <span>Shipping</span>
            <span>Free</span>
          </div>
          <div className={styles.summaryTotal}>
            <span>Grand Total</span>
            <span>{formatPrice(total)}</span>
          </div>

          <form onSubmit={handleSubmitOrder} className={styles.customerForm}>
            <h3>Delivery Details</h3>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.formGroup}>
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={customerInfo.name}
                onChange={handleInputChange}
                placeholder="Full Name"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={customerInfo.phone}
                onChange={handleInputChange}
                placeholder="Phone Number"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                name="address"
                value={customerInfo.address}
                onChange={handleInputChange}
                placeholder="Detailed Address"
                required
              />
            </div>
            <button 
              type="submit" 
              className={styles.checkoutButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting Order...' : 'Complete Purchase'}
            </button>
          </form>

          <Link to="/shop" className={styles.continueShoppingLink}>
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Cart;